import dayjs from '@skynet/formatting-date';
import type { Dayjs } from '@skynet/formatting-date';

/**
 * A naive "YYYY-MM-DD HH:mm" string carrying the user's *local* calendar day
 * and wall-clock time — never a real UTC/Z instant. `comparisonStartDate` /
 * `comparisonEndDate` and the synthetic "current" history item's `date` (see
 * `buildCurrentSnapshot` in `transformations/history.ts`) are this shape.
 * Real persisted `JsonBalanceHistoryItem.date` values are NOT this shape —
 * they carry a genuine Z-suffixed UTC instant from the backend and must be
 * converted with plain `dayjs(...)` before being compared against one of
 * these (SKY-4575). This alias exists purely to flag that convention at the
 * call site; it is not enforced by the type system.
 */
export type NaiveLocalDateTime = string;

// `date` is a naive "YYYY-MM-DD HH:mm" string carrying the user's local calendar
// day (see useInitBalanceState.tsx / useComparisonDates.ts), parsed here via
// dayjs.utc() only to read its digits back out without a local-offset shift.
// Comparing that to a real UTC/local "now" instant via isSame(..., 'date') is
// an apples-to-oranges epoch comparison that rolls back a day whenever the
// user's local date is ahead of UTC (SKY-4575) — comparing formatted calendar
// strings instead sidesteps the offset entirely. Only strings are accepted: a
// Dayjs built from local time would be shifted by the offset when re-read here.
export const isComparisonDateNow = (date: NaiveLocalDateTime) =>
  dayjs.utc(date).format('YYYY-MM-DD') === dayjs().format('YYYY-MM-DD');

export const getComparisonTimeOptionValue = (comparisonDate: Dayjs | string) => {
  const historyItemDateDayjs = dayjs(comparisonDate);
  const tomorrow = dayjs().add(1, 'day').startOf('day');

  const correctedItemDate =
    historyItemDateDayjs.format('HH:mm') === '23:59'
      ? historyItemDateDayjs.add(1, 'minute')
      : historyItemDateDayjs;

  if (correctedItemDate.isSame(tomorrow, 'date')) {
    return 'current';
  }

  return correctedItemDate.format('HH:mm');
};
